import React, { useState } from 'react'
import { graphql } from 'gatsby'
import style from './accordion.mod.scss'
import { motion } from 'framer-motion'
import { Content, Button } from '@components'
import { FaChevronDown } from 'react-icons/fa'
import { GatsbyImage } from 'gatsby-plugin-image'

const AccordionContent = ({
  heading,
  content,
  items,
  button,
  headingColour,
  expanded,
  setExpanded
}) => (
  <>
    {heading && (
      <div className={style[`accordion--heading-color-${headingColour}`]}>
        <h5>{heading}</h5>
      </div>
    )}
    {content && (
      <div
        className={style.accordion__body}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    )}
    {items.map(({ title, description }, i) => {
      const isOpen = i === expanded
      return (
        <div className={style.accordion__item} key={`accordion-item-${i}`}>
          <a
            href={`#accordion-item-${i}`}
            onClick={e => {
              e.preventDefault()
              setExpanded(isOpen ? false : i)
            }}
            className={style.accordion__header}
          >
            <div className={style[`accordion--heading-color-${headingColour}`]}>
              <h6>{title}</h6>
            </div>
            <span className={style.accordion__arrow}>
              <motion.span
                className={style.accordion__icon}
                animate={isOpen ? 'open' : 'collapsed'}
                initial={'collapsed'}
                variants={{
                  open: { rotate: 180 },
                  collapsed: { rotate: 0 }
                }}
              >
                <FaChevronDown />
              </motion.span>
            </span>
          </a>

          <motion.div
            layoutTransition
            useInvertedScale
            key={`accordion-item-motion-${i}`}
            id={`accordion-item-${i}`}
            animate={isOpen ? 'open' : 'collapsed'}
            initial={'collapsed'}
            variants={{
              open: { height: '100%', opacity: 1, display: 'block' },
              collapsed: {
                height: '0',
                opacity: 0,
                display: 'none',
                transition: {
                  opacity: {
                    duration: 0.2
                  },
                  display: {
                    duration: 0.2
                  }
                }
              }
            }}
          >
            {description && (
              <div className={style.accordion__content}>
                <Content content={description} />
              </div>
            )}
          </motion.div>
        </div>
      )
    })}
    {(button?.link?.path || button?.url) && (
      <div className={style.accordion__button}>
        <Button
          size="large"
          alignment="center"
          buttonStyle={'secondary'}
          to={button?.link?.path || button?.url}
        >
          {button.displayText}
        </Button>
      </div>
    )}
  </>
)

const Accordion = ({
  items,
  heading,
  content,
  textAlign,
  button,
  headingColour,
  theme,
  leftTitle,
  leftDescription,
  leftButton,
  leftImage,
  collapseFirst
}) => {
  const [expanded, setExpanded] = useState(collapseFirst ? -1 : 0)
  const classes = [style.accordion]
  if (theme) {
    classes.push(`${style.accordion}--theme-${theme}`)
  }
  textAlign === 'Centre' && classes.push(`${style.accordion}--center`)

  const hasLeftContent = leftTitle || leftDescription || leftButton || leftImage
  hasLeftContent && classes.push(style['accordion--has-left-content'])

  console.log('leftImage', leftImage)

  return (
    <div className={[...classes].join(' ')}>
      {hasLeftContent ? (
        <div className={style.accordion__split}>
          <div className={style.accordion__left}>
            {leftTitle && (
              <div
                className={style[`accordion--heading-color-${headingColour}`]}
              >
                <h5>{leftTitle}</h5>
              </div>
            )}
            {leftDescription && (
              <div className={style.accordion__body}>
                <Content content={leftDescription} />
              </div>
            )}
            {leftImage && (
              <div className={style.accordion__image}>
                <img
                  src={leftImage.gatsbyImageData.images.fallback.src}
                  alt={leftImage.alt || ''}
                />
              </div>
            )}
            {leftButton && (
              <div className={style.accordion__button}>
                <Button
                  size="large"
                  alignment="center"
                  buttonStyle={'secondary'}
                  to={leftButton?.link?.path || leftButton?.url}
                >
                  {leftButton.displayText}
                </Button>
              </div>
            )}
          </div>
          <div className={style.accordion__right}>
            <AccordionContent
              heading={heading}
              content={content}
              items={items}
              button={button}
              headingColour={headingColour}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          </div>
        </div>
      ) : (
        <AccordionContent
          heading={heading}
          content={content}
          items={items}
          button={button}
          headingColour={headingColour}
          expanded={expanded}
          setExpanded={setExpanded}
        />
      )}
    </div>
  )
}

export default Accordion

export const query = graphql`
  fragment Accordion on DatoCmsAccordion {
    id
    model {
      apiKey
    }
    paddingTop
    paddingBottom
    __typename
    heading
    headingColour
    button {
      ...Link
    }
    content
    textAlign
    items {
      title
      description
    }
    leftTitle
    leftDescription
    leftButton {
      ...Link
    }
    leftImage {
      gatsbyImageData(
        layout: CONSTRAINED
        imgixParams: { h: "400", fit: "crop", w: "400", q: 80 }
      )
      alt
    }
    collapseFirst
  }
`
